import type { z } from "zod";

import {
  DesktopAvailabilityResultSchema,
  DesktopCapabilityActivationResultSchema,
  type DesktopCapabilityActivationResult,
  type DesktopFrameMessage,
} from "../../shared/schemas";
import type { DesktopAvailabilityDataSchema } from "../../shared/schemas";
import { ApiClientError, createDesktopCapabilityChallenge } from "../api/client";
import type { ActiveAppFrame } from "../app-frames";
import {
  activationError,
  correlateResult,
  DESKTOP_FRAME_BROKER_DEADLINES_MS,
  DesktopFrameDeadlineError,
  type DesktopPreloadApi,
  type OwnedSession,
  withDeadline,
} from "./broker-core";

type ActivationMessage = Extract<
  DesktopFrameMessage,
  { type: "platform:desktop-capability-request" }
>;
type AvailableDevice = Extract<z.infer<typeof DesktopAvailabilityDataSchema>, { available: true }>;
type ActivationOutcome =
  | { stale: true }
  | {
      stale: false;
      result: DesktopCapabilityActivationResult;
      ownedSession?: OwnedSession;
    };

interface ActivationOptions {
  active: ActiveAppFrame;
  api: DesktopPreloadApi | null;
  closeSession: (sessionHandle: string) => Promise<void>;
  frameIsCurrent: () => boolean;
  message: ActivationMessage;
  source: WindowProxy;
}

export async function activateDesktopCapability(
  options: ActivationOptions,
): Promise<ActivationOutcome> {
  if (!options.api) return result(unavailableResult(options.message.requestId));
  const availability = await readAvailability(options.api, options.message.requestId);
  if (!options.frameIsCurrent()) return { stale: true };
  if (!availability.ok) return result(availability.result);
  return activateAvailableDevice(options, availability.device);
}

type AvailabilityOutcome =
  | { ok: true; device: AvailableDevice }
  | { ok: false; result: DesktopCapabilityActivationResult };

async function readAvailability(
  api: DesktopPreloadApi,
  requestId: string,
): Promise<AvailabilityOutcome> {
  let rawAvailability: unknown;
  try {
    rawAvailability = await withDeadline(
      api.getAvailability(),
      DESKTOP_FRAME_BROKER_DEADLINES_MS.availability,
      {
        code: "DESKTOP_TIMEOUT",
        message: "CCBox Desktop did not answer the availability check in time.",
      },
    );
  } catch (error) {
    if (error instanceof DesktopFrameDeadlineError) {
      return { ok: false, result: activationError(error.code, error.message, requestId, true) };
    }
    rawAvailability = null;
  }
  const availability = DesktopAvailabilityResultSchema.safeParse(rawAvailability);
  if (!availability.success || !availability.data.ok) {
    return failure(
      "DESKTOP_UNAVAILABLE",
      "CCBox Desktop could not verify its local bridge.",
      requestId,
      true,
    );
  }
  if (!availability.data.data.available) {
    return { ok: false, result: unavailableDeviceResult(availability.data.data.reason, requestId) };
  }
  return { ok: true, device: availability.data.data };
}

async function activateAvailableDevice(
  options: ActivationOptions,
  device: AvailableDevice,
): Promise<ActivationOutcome> {
  try {
    const challenge = await requestChallenge(options, device);
    if (!options.frameIsCurrent()) return { stale: true };
    const activation = await requestActivation(options, challenge.challengeId);
    const parsed = DesktopCapabilityActivationResultSchema.safeParse(activation);
    if (!parsed.success) {
      return result(
        activationError(
          "DESKTOP_UNAVAILABLE",
          "CCBox Desktop returned an invalid capability response.",
          options.message.requestId,
          true,
        ),
      );
    }
    return await validateActivation(options, device, parsed.data);
  } catch (error) {
    return result(mapActivationError(error, options.message.requestId));
  }
}

async function requestChallenge(options: ActivationOptions, device: AvailableDevice) {
  return withDeadline(
    createDesktopCapabilityChallenge({
      protocolVersion: 1,
      appInstanceId: options.active.appInstanceId,
      deviceId: device.deviceId,
      deviceDisplayName: device.deviceDisplayName,
      platform: device.platform,
      architecture: device.architecture,
      keyVersion: device.keyVersion,
      publicKeySpki: device.publicKeySpki,
      binding: options.message.binding,
      operations: options.message.operations,
    }),
    DESKTOP_FRAME_BROKER_DEADLINES_MS.controlPlane,
    {
      code: "CONTROL_PLANE_TIMEOUT",
      message: "CCBox could not reach the desktop authorization service in time.",
    },
  );
}

async function requestActivation(
  options: ActivationOptions,
  challengeId: string,
): Promise<unknown> {
  const activation = options.api?.activateCapability({ protocolVersion: 1, challengeId });
  if (!activation) throw new Error("Desktop bridge unavailable");
  try {
    return await withDeadline(activation, DESKTOP_FRAME_BROKER_DEADLINES_MS.activation, {
      code: "DESKTOP_TIMEOUT",
      message: "CCBox Desktop did not finish capability activation in time.",
    });
  } catch (error) {
    if (error instanceof DesktopFrameDeadlineError)
      closeLateActivation(activation, options.closeSession);
    throw error;
  }
}

function closeLateActivation(
  activation: Promise<unknown>,
  closeSession: (sessionHandle: string) => Promise<void>,
): void {
  void activation
    .then(async (lateResult) => {
      const parsed = DesktopCapabilityActivationResultSchema.safeParse(lateResult);
      if (parsed.success && parsed.data.ok) await closeSession(parsed.data.data.sessionHandle);
    })
    .catch(() => undefined);
}

async function validateActivation(
  options: ActivationOptions,
  device: AvailableDevice,
  activation: DesktopCapabilityActivationResult,
): Promise<ActivationOutcome> {
  if (activation.ok && !device.supportedCapabilities.includes(activation.data.capability)) {
    await options.closeSession(activation.data.sessionHandle);
    return result(
      activationError(
        "DESKTOP_UPGRADE_REQUIRED",
        "CCBox Desktop did not advertise support for the activated capability.",
        options.message.requestId,
      ),
    );
  }
  const correlated = correlateResult(activation, options.message.requestId);
  if (!options.frameIsCurrent()) {
    if (correlated.ok) await options.closeSession(correlated.data.sessionHandle);
    return { stale: true };
  }
  if (!correlated.ok) return result(correlated);
  return {
    stale: false,
    result: correlated,
    ownedSession: {
      source: options.source,
      frame: options.active,
      capability: correlated.data.capability,
      allowedOperations: correlated.data.allowedOperations,
    },
  };
}

function mapActivationError(error: unknown, requestId: string): DesktopCapabilityActivationResult {
  if (error instanceof DesktopFrameDeadlineError) {
    return activationError(error.code, error.message, requestId, true);
  }
  if (!(error instanceof ApiClientError)) {
    return activationError(
      "CONTROL_PLANE_UNAVAILABLE",
      "CCBox could not authorize this desktop capability.",
      requestId,
      true,
    );
  }
  const code =
    error.code === "desktop_capability_denied"
      ? "CAPABILITY_DENIED"
      : error.code === "authentication_required"
        ? "SESSION_REVOKED"
        : "CONTROL_PLANE_UNAVAILABLE";
  return activationError(
    code,
    error.message,
    requestId,
    error.status === 429 || error.status >= 500,
  );
}

function unavailableResult(operationId: string): DesktopCapabilityActivationResult {
  return activationError(
    "DESKTOP_REQUIRED",
    "Open this tool in CCBox Desktop to use this capability.",
    operationId,
  );
}

function unavailableDeviceResult(
  reason: "browser" | "device_unavailable" | "key_unavailable" | "unsupported_version",
  operationId: string,
): DesktopCapabilityActivationResult {
  if (reason === "browser") return unavailableResult(operationId);
  if (reason === "unsupported_version") {
    return activationError(
      "DESKTOP_UPGRADE_REQUIRED",
      "Update CCBox Desktop before using this capability.",
      operationId,
    );
  }
  const keyUnavailable = reason === "key_unavailable";
  return activationError(
    keyUnavailable ? "DEVICE_STORAGE_UNAVAILABLE" : "DESKTOP_UNAVAILABLE",
    keyUnavailable
      ? "Protected device identity is unavailable on this workstation."
      : "Local device access is unavailable on this workstation.",
    operationId,
    true,
  );
}

function failure(
  code: string,
  message: string,
  requestId: string,
  retryable: boolean,
): AvailabilityOutcome {
  return { ok: false, result: activationError(code, message, requestId, retryable) };
}

function result(resultValue: DesktopCapabilityActivationResult): ActivationOutcome {
  return { stale: false, result: resultValue };
}
